import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Chip,
  Container,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DOMPurify from "dompurify";
import { getTranslation } from "common";
import { useMonitor, usePriorities, useSelectedOrganization, useTranslations } from "hooks";
import ProcessItem, { StartedProcess } from "./ProcessItem";
import AutoSizer from "react-virtualized-auto-sizer";
import { VirtualTable } from "./VirtualizedTable";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { getUserData, SortCriterion } from "common/utilities";
import { SortState } from "hooks/useSortableList";
import { ConfirmDialog, TaskDetailDialog } from "./dialogs";
import { useSnackbar } from "hooks";
import { processesApi } from "api";
import { TaskDetail } from "./TaskItem";
import NoMonitor from "image/NoMonitor.png";
import { BaseTask } from "model/activitiesModel";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

type RowProps = {
  index: number;
  style: Object;
};

interface FilterChip {
  label: string;
  onDelete: () => void;
}

interface ProcessesListProps {
  title: string;
  processes: Array<StartedProcess>;
  filters: Record<string, any>;
  filterChips: FilterChip[];
  refresh: () => Promise<StartedProcess[] | undefined>;
  handleChangeSort: (key: string) => void;
  newSort: SortCriterion;
  filteredUserDataKeys: string[];
  history: boolean;
}
const ProcessesList: React.FC<ProcessesListProps> = ({
  processes = [],
  title,
  filters,
  filterChips,
  refresh,
  handleChangeSort,
  newSort,
  filteredUserDataKeys,
  history
}) => {
  const translations = useTranslations();
  const [selectedTask, setSelectedTask] = useState<BaseTask | null>(null);
  const selectedOrganization = useSelectedOrganization();
  const isAdmin = selectedOrganization?.administrator;
  const [allPriorities, loadingPriorities] = usePriorities(selectedOrganization?.id || null);

  const [confirmData, setConfirmData] = useState<null | {
    task: TaskDetail;
    type: "delete" | "terminate" | "disclose" | "reschedule";
  }>(null);
  const [hasDate, setHasDate] = useState<boolean>(false);
  const [expandedCells, setExpandedCells] = useState<Record<string, boolean>>({});
  const [fullContentDialog, setFullContentDialog] = useState<{
    open: boolean;
    content: string;
    title: string;
  }>({ open: false, content: "", title: "" });
  const {
    monitors,
    isLoading: isMonitorsLoading,
    getMonitors,
  } = useMonitor(selectedOrganization?.id);

  const toggleExpand = React.useCallback((key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCells(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const openFullContentDialog = React.useCallback((content: string, title: string) => {
    setFullContentDialog({ open: true, content, title });
  }, []);

  const closeFullContentDialog = React.useCallback(() => {
    setFullContentDialog({ open: false, content: "", title: "" });
  }, []);

  const Row: React.FC<RowProps> = React.useMemo(
    () =>
      React.memo(({ index }: RowProps) => {
        const process = processes[index];
        return (
          <ProcessItem
            setSelectedTask={setSelectedTask}
            handleClick={(task, type) => setConfirmData({ task, type })}
            key={process.idprocesstoken}
            monitors={monitors}
            isMonitorsLoading={isMonitorsLoading}
            filters={filters}
            process={process}
            userDataKeys={filteredUserDataKeys}
            refresh={refresh}
            // expandedCells={expandedCells}
            // toggleExpand={toggleExpand}
            openFullContentDialog={openFullContentDialog}
            allPriorities={allPriorities}
            loadingPriorities={loadingPriorities}
            history={history}
          />
        );
      }),
    [filteredUserDataKeys, filters, processes, monitors, isMonitorsLoading, openFullContentDialog, setSelectedTask, setConfirmData, refresh, allPriorities, loadingPriorities, history]
  );

  const { sendSnack } = useSnackbar();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);



  useEffect(() => {
    if (confirmData?.type === "reschedule") {
      const taskTimer = monitors.find(
        (timer) => timer.idprocesstoken === confirmData?.task.idprocesstoken
      );
      if (taskTimer) {
        setSelectedDate(new Date(taskTimer.starttime));
        setHasDate(true);
      }
    }
  }, [confirmData, monitors]);


  useEffect(() => {
    if (confirmData?.type === "reschedule") {
      const taskTimer = monitors.find(
        (timer) => timer.idprocesstoken === confirmData?.task.idprocesstoken
      );
      if (taskTimer) {
        // console.log(taskTimer.starttime);
        setSelectedDate(new Date(taskTimer.starttime));
        setHasDate(true);
      }
    }
  }, [confirmData, monitors]);

  const handleActionConfirm = async () => {
    try {
      if (confirmData?.type === "terminate") {
        const { data } = await processesApi.post(
          `/${confirmData?.task.idprocess}/terminate`
        );
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      } else if (confirmData?.type === "delete") {
        const { data } = await processesApi.delete(
          `/${confirmData?.task.idprocess}`
        );
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      } else if (confirmData?.type === "disclose") {
        const { data } = await processesApi.post(
          `/${confirmData?.task.idprocesstoken}/intermediateevents/terminate`
        );
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      } else if (confirmData?.type === "reschedule") {
        const now = new Date();
        if (!selectedDate || selectedDate < now) {
          sendSnack({
            message: getTranslation(
              translations,
              "error.date_past.snackbar.text"
            ),
            type: "error",
          });
          return;
        }
        const UTCDate = selectedDate.toISOString();
        const { data } = await processesApi.post(
          `/${confirmData?.task.idprocesstoken}/intermediateevents/reorganize`,
          { date: UTCDate }
        );
        getMonitors();
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      }
      refresh();
    } catch (error) {
    } finally {
      setConfirmData(null);
    }
  };

  const fromDoingTo_Todo = async ({
    idprocesstoken,
  }: {
    idprocesstoken: number;
  }) => {
    try {
      await processesApi.post(`/${idprocesstoken}/undo`);
    } catch {
    } finally {
    }
  };

  const task = processes.find(
    (a) => a.idprocesstoken === selectedTask?.idprocesstoken
  ) as StartedProcess;

  return (
    <React.Fragment>
      <TaskDetailDialog
        readonly={!isAdmin || history}
        historyMode={history}
        handleBackStep={
          selectedTask?.state === 1 && isAdmin
            ? (task) => {
              fromDoingTo_Todo(task);
              setSelectedTask(null);
              refresh();
            }
            : undefined
        }
        open={Boolean(selectedTask)}
        onClose={() => setSelectedTask(null)}
        doing
        refresh={async () => {
          const newProcesses = await refresh();
          setSelectedTask((_process) => {
            const task =
              newProcesses?.find(
                (a) => _process?.idprocesstoken === a.idprocesstoken
              ) || null;
            return task;
          });
        }}
        task={{
          ...task,
          userdataLength: Object.keys(getUserData(task?.process_userdata))
            .length,
          userschema: task?.userschema,
          idprocessmodel: task?.process_idprocessmodel,
          idorganization: selectedOrganization?.id || 0,
          description: task?.process_description,
          assignmentmode: 0,
          idswimlane: 0,
          designerid: "",
          userdata: task?.process_userdata,
          jsontag: JSON.stringify(task?.jsontag),
          priority_name: "",
          jsonparticipant: "",
          countcomments: 0,
          countattachments: 0,
          countuserdatafields: 0
        }}
      />
      <ConfirmDialog
        open={confirmData?.type === "terminate"}
        onClose={() => setConfirmData(null)}
        onConfirm={handleActionConfirm}
        cancelText={getTranslation(translations, "generic.button.cancel")}
        confirmText={getTranslation(translations, "generic.button.confirm")}
        title={getTranslation(translations, "generic.button.confirm")}
        content={getTranslation(translations, "general.terminate.text")}
      />
      <ConfirmDialog
        open={confirmData?.type === "delete"}
        onClose={() => setConfirmData(null)}
        onConfirm={handleActionConfirm}
        cancelText={getTranslation(translations, "generic.button.cancel")}
        confirmText={getTranslation(translations, "generic.button.confirm")}
        title={getTranslation(translations, "generic.button.confirm")}
        content={getTranslation(translations, "general.delete.text")}
      />
      <ConfirmDialog
        open={confirmData?.type === "disclose"}
        onClose={() => setConfirmData(null)}
        onConfirm={handleActionConfirm}
        cancelText={getTranslation(translations, "generic.button.cancel")}
        confirmText={getTranslation(translations, "generic.button.confirm")}
        title={getTranslation(translations, "generic.button.confirm")}
        content={getTranslation(translations, "general.disclose.text")}
      />
      <ConfirmDialog
        open={confirmData?.type === "reschedule"}
        onClose={() => setConfirmData(null)}
        onConfirm={handleActionConfirm}
        cancelText={getTranslation(translations, "generic.button.cancel")}
        confirmText={getTranslation(translations, "generic.button.confirm")}
        title={getTranslation(translations, "generic.button.confirm")}
        hasDate={hasDate}
        type="reschedule"
        content={
          <DateTimePicker
            value={dayjs(selectedDate)}
            label={getTranslation(translations, "general.date_and_time")}
            onChange={(v) => {
              if (v) {
                setSelectedDate(v.toDate());
                setHasDate(true);
              }
            }}
            slotProps={{
              textField: { size: "small" },
              popper: {
                placement: "bottom",
                modifiers: [
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      altAxis: true,
                      altBoundary: true,
                      tether: true,
                      rootBoundary: true,
                      padding: 8,
                    },
                  },
                  {
                    name: "flip",
                    enabled: true,
                    options: {
                      altBoundary: true,
                      rootBoundary: "document",
                      padding: 8
                    },
                  },
                ],
              },
            }}
            sx={{
              width: 250,
              mt: 1.5
            }}
          />
        }
      />
      <ConfirmDialog
        open={fullContentDialog.open}
        onClose={closeFullContentDialog}
        onConfirm={closeFullContentDialog}
        //  cancelText={getTranslation(translations, "generic.button.cancel")}
        confirmText={getTranslation(translations, "pm.minimap.close.lable")}
        title={fullContentDialog.title}
        maxWidth="lg"
        fullWidth={true}
        content={
          <Box sx={{ whiteSpace: "pre-wrap", maxHeight: "600px", minHeight: "300px" }}>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fullContentDialog.content) }} />
          </Box>
        }
      />
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6">{title}</Typography>
          {filterChips.map((chip, index) => (
            <Chip
              key={index}
              label={chip.label}
              size="small"
              onDelete={chip.onDelete}
            />
          ))}
        </Stack>
      </Stack>
      <div
        style={{
          height: `calc(100% - 120px)`,
          maxHeight: `calc(100% - 120px)`,
        }}
      >
        {processes.length <= 0 ? (
          <Container
            sx={{
              display: "flex",
              height: "100%",
              flexDirection: "column",
              gap: 2,
              justifyContent: "center",
              alignItems: "center",
            }}
            maxWidth="md"
          >
            <Box
              component="img"
              height="50dvh"
              width="auito"
              sx={{ borderRadius: 9999 }}
              src={NoMonitor}
              alt="no scheduled process"
            />
            <Typography fontWeight="bold">
              {getTranslation(translations, "generic.nomonitor.message")}
            </Typography>
          </Container>
        ) : history ? (
          <Box sx={{ overflow: "auto", height: "100%" }}>
            <Table stickyHeader>
              <TableHead sx={{ "& th": { fontWeight: "bold", borderBottom: "none", backgroundColor: "#eeeeee" } }}>
                <TableRow>
                  <TableCell sx={{ width: "15%", position: "sticky", left: 0, zIndex: 999, backgroundColor: "#eeeeee" }}>
                    <Stack flexDirection="row" spacing={1} alignItems="center">
                      <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.process")}</Typography>
                      <IconButton sx={{ opacity: newSort.model ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("model")}>
                        {newSort.model === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </TableCell>
                  {filteredUserDataKeys.map((key) => (
                    <TableCell key={key}>
                      <Stack flexDirection="row" spacing={1} alignItems="center">
                        <Tooltip title={key}>
                          <Typography sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }} fontWeight="bold">{key}</Typography>
                        </Tooltip>
                        <IconButton sx={{ opacity: newSort[key] ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort(key)}>
                          {newSort[key] === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                        </IconButton>
                      </Stack>
                    </TableCell>
                  ))}
                  {!history && (
                    <TableCell sx={{ width: "15%" }}>
                      <Stack flexDirection="row" spacing={1} alignItems="center">
                        <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.task")}</Typography>
                        <IconButton sx={{ opacity: newSort.activity ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("activity")}>
                          {newSort.activity === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                        </IconButton>
                      </Stack>
                    </TableCell>
                  )}
                  <TableCell sx={{ width: "15%" }}>
                    <Stack flexDirection="row" spacing={1} alignItems="center">
                      <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.process.duration")}</Typography>
                      <IconButton sx={{ opacity: newSort.processduration ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("processduration")}>
                        {newSort.processduration === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </TableCell>
                  {!history && (
                    <TableCell sx={{ width: "15%" }}>
                      <Stack flexDirection="row" spacing={1} alignItems="center">
                        <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.waiting_time.caption")}</Typography>
                        <IconButton sx={{ opacity: newSort.timetotakeover ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("timetotakeover")}>
                          {newSort.timetotakeover === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                        </IconButton>
                      </Stack>
                    </TableCell>
                  )}
                  {!history && (
                    <TableCell sx={{ width: "15%" }}>
                      <Stack flexDirection="row" spacing={1} alignItems="center">
                        <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.working_time.caption")}</Typography>
                        <IconButton sx={{ opacity: newSort.timeindoing ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("timeindoing")}>
                          {newSort.timeindoing === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                        </IconButton>
                      </Stack>
                    </TableCell>
                  )}
                  <TableCell sx={{ width: "10%" }}>
                    <Stack flexDirection="row" spacing={1} alignItems="center">
                      <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.priorities.caption")}</Typography>
                      <IconButton sx={{ opacity: newSort.priorityorder ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("priorityorder")}>
                        {newSort.priorityorder === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ width: "20%" }}>
                    {getTranslation(translations, "generic.participants")}
                  </TableCell>
                  <TableCell sx={{ width: "5%" }}>
                    {getTranslation(translations, "generic.collapse.title")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processes.map((process) => (
                  <ProcessItem
                    key={process.idprocess}
                    setSelectedTask={setSelectedTask}
                    handleClick={(task, type) => setConfirmData({ task, type })}
                    monitors={monitors}
                    isMonitorsLoading={isMonitorsLoading}
                    filters={filters}
                    process={process}
                    userDataKeys={filteredUserDataKeys}
                    refresh={refresh}
                    openFullContentDialog={openFullContentDialog}
                    allPriorities={allPriorities}
                    loadingPriorities={loadingPriorities}
                    history={history}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <AutoSizer style={{ width: "100%" }}>
            {({
              height,
            }: {
              height: number;
              width: number;
              scaledWidth: number;
              scaledHeight: number;
            }) => (
              <React.Fragment>
                <VirtualTable
                  height={height}
                  width="100%"
                  itemCount={processes.length}
                  itemSize={120}
                  header={
                    <TableHead sx={{ "& th": { fontWeight: "bold", borderBottom: "none", backgroundColor: "#eeeeee" } }}>
                      <TableRow
                        sx={{
                          "& > th": {
                            // borderTop: "1px solid #ddd",
                          },
                          //escamotage incredibile per fare gli angoli arrotondati sulla tabella virtualizzata (by claude.ai);
                          //con border radius non riuscivo perché i record finiscono per sovrapporsi alla riga di header
                          "& > th:first-of-type": {
                            // borderTopLeftRadius: 40,
                            borderLeft: "1px solid #ddd",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: -2,
                              left: -1,
                              width: 40,
                              height: 40,
                              background: "radial-gradient(circle at bottom right, transparent 40px, white 40px)",
                            },
                          },
                          //uguale per l'ultimo
                          "& > th:last-child": {
                            // borderTopRightRadius: 40,
                            borderRight: "1px solid #ddd",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              top: -2,
                              right: -1,
                              width: 40,
                              height: 40,
                              background: "radial-gradient(circle at bottom left, transparent 40px, white 40px)",
                            },
                          },
                        }}
                      >
                        <TableCell sx={{ width: "15%", position: "sticky", left: 0, zIndex: 99999, backgroundColor: "#eeeeee" }} id="process-header" >
                          <Stack
                            flexDirection="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography style={{ fontWeight: "bold", }}>
                              {getTranslation(translations, "generic.process")}
                            </Typography>
                            <IconButton
                              sx={{
                                opacity: newSort.model ? 1 : 0.3,
                                mt: "0px !important",
                              }}
                              size="small"
                              onClick={() => handleChangeSort("model")}
                            >
                              {newSort.model === SortState.Asc ? (
                                <ArrowUpward fontSize="small" />
                              ) : (
                                <ArrowDownward fontSize="small" />
                              )}
                            </IconButton>
                          </Stack>
                        </TableCell>
                        {filteredUserDataKeys.map((key) => (
                          <TableCell key={key}>
                            <Stack
                              flexDirection="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Tooltip title={key}>
                                <Typography
                                  sx={{
                                    maxWidth: 150,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  fontWeight="bold"
                                >
                                  {key}
                                </Typography>
                              </Tooltip>
                              <IconButton
                                sx={{
                                  opacity: newSort[key] ? 1 : 0.3,
                                  mt: "0px !important",
                                }}
                                size="small"
                                onClick={() => handleChangeSort(key)}
                              >
                                {newSort[key] === SortState.Asc ? (
                                  <ArrowUpward fontSize="small" />
                                ) : (
                                  <ArrowDownward fontSize="small" />
                                )}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        ))}
                        {!history && (
                          <TableCell sx={{ width: "15%" }}>
                            <Stack
                              flexDirection="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography style={{ fontWeight: "bold" }}>
                                {getTranslation(translations, "generic.task")}
                              </Typography>
                              <IconButton
                                sx={{
                                  opacity: newSort.activity ? 1 : 0.3,
                                  mt: "0px !important",
                                }}
                                size="small"
                                onClick={() => handleChangeSort("activity")}
                              >
                                {newSort.activity === SortState.Asc ? (
                                  <ArrowUpward fontSize="small" />
                                ) : (
                                  <ArrowDownward fontSize="small" />
                                )}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        )}
                        <TableCell sx={{ width: "15%" }}>
                          <Stack
                            flexDirection="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography style={{ fontWeight: "bold" }}>
                              {getTranslation(translations, "generic.process.duration")}
                            </Typography>
                            <IconButton
                              sx={{
                                opacity: newSort.processduration ? 1 : 0.3,
                                mt: "0px !important",
                              }}
                              size="small"
                              onClick={() => handleChangeSort("processduration")}
                            >
                              {newSort.processduration === SortState.Asc ? (
                                <ArrowUpward fontSize="small" />
                              ) : (
                                <ArrowDownward fontSize="small" />
                              )}
                            </IconButton>
                          </Stack>
                        </TableCell>
                        {!history && (
                          <TableCell sx={{ width: "15%" }}>
                            <Stack
                              flexDirection="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography style={{ fontWeight: "bold" }}>
                                {getTranslation(translations, "generic.waiting_time.caption")}
                              </Typography>
                              <IconButton
                                sx={{
                                  opacity: newSort.timetotakeover ? 1 : 0.3,
                                  mt: "0px !important",
                                }}
                                size="small"
                                onClick={() => handleChangeSort("timetotakeover")}
                              >
                                {newSort.timetotakeover === SortState.Asc ? (
                                  <ArrowUpward fontSize="small" />
                                ) : (
                                  <ArrowDownward fontSize="small" />
                                )}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        )}
                        {!history && (
                          <TableCell sx={{ width: "15%" }}>
                            <Stack
                              flexDirection="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography style={{ fontWeight: "bold" }}>
                                {getTranslation(translations, "generic.working_time.caption")}
                              </Typography>
                              <IconButton
                                sx={{
                                  opacity: newSort.timeindoing ? 1 : 0.3,
                                  mt: "0px !important",
                                }}
                                size="small"
                                onClick={() => handleChangeSort("timeindoing")}
                              >
                                {newSort.timeindoing === SortState.Asc ? (
                                  <ArrowUpward fontSize="small" />
                                ) : (
                                  <ArrowDownward fontSize="small" />
                                )}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        )}
                        {/* {(filters.priorities?.length || 0) > 0 && ( */}
                        <TableCell sx={{ width: "10%" }}>
                          <Stack
                            flexDirection="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography style={{ fontWeight: "bold" }}>
                              {getTranslation(translations, "generic.priorities.caption")}
                            </Typography>
                            <IconButton
                              sx={{
                                opacity: newSort.priorityorder ? 1 : 0.3,
                                mt: "0px !important",
                              }}
                              size="small"
                              onClick={() => handleChangeSort("priorityorder")}
                            >
                              {newSort.priorityorder === SortState.Asc ? (
                                <ArrowUpward fontSize="small" />
                              ) : (
                                <ArrowDownward fontSize="small" />
                              )}
                            </IconButton>
                          </Stack>
                        </TableCell>
                        {/* )} */}
                        {!history && (
                          <TableCell sx={{ width: "10%" }}>
                            {getTranslation(
                              translations,
                              "pm.label.process_status"
                            )}
                          </TableCell>)}
                        <TableCell sx={{ width: "20%" }}>
                          {getTranslation(
                            translations,
                            "generic.participants"
                          )}
                        </TableCell>
                        {!history && (
                          <TableCell sx={{ width: "5%" }}>
                            {getTranslation(translations, "generic.action.title")}
                          </TableCell>)}
                        {history && (
                          <TableCell sx={{ width: "5%" }}>
                            {getTranslation(translations, "generic.collapse.title")}
                          </TableCell>)}
                      </TableRow>
                    </TableHead>
                  }
                  row={Row}
                />
              </React.Fragment>
            )}
          </AutoSizer>
        )}
      </div>
    </React.Fragment>
  );
};

export default ProcessesList;
